import { ComponentRegistry } from '../util/ComponentRegistry';

type ShopSwitchHook = () => void | Promise<void>;
type ShopSwitchFailHook = (error: unknown) => void | Promise<void>;
type ShopSwitchOptions = {
    onClick?: ShopSwitchHook;
    onSuccess?: ShopSwitchHook;
    onFail?: ShopSwitchFailHook;
    checkCartContentsUrl?: string;
    clearCartContentsUrl?: string;
    requestVerificationToken?: string;
};

type CartContentsResult = { hasItems: boolean };
type ClearCartContentsResult = { success: boolean };

export class ShopSwitch {
    private readonly id: string;
    private static registry = new ComponentRegistry<ShopSwitch>('shop-switch');
    private readonly trigger: HTMLButtonElement | HTMLAnchorElement;
    private newShopUrl: string;

    private constructor(
        trigger: HTMLButtonElement | HTMLAnchorElement,
        private options: ShopSwitchOptions
    ) {
        this.id = ShopSwitch.registry.register(trigger, this);
        this.trigger = trigger;
        this.newShopUrl = trigger.getAttribute('href') || '';
        this.setClickEvent();
    }

    private setClickEvent() {
        this.trigger.addEventListener('click', async (e) => {
            e.preventDefault(); 

            if (typeof this.options.onClick === 'function') {
                await this.options.onClick();
            }

            // Allow current store to pass through as-is
            if(this.trigger.classList.contains('active')) { 
                window.location.href = this.newShopUrl;
                return;
            }

            try {
                const { hasItems } = await this.checkCartContents();
                
                if (hasItems) {
                    await this.displayWarningModal();
                } else {
                    window.location.href = this.newShopUrl;
                }
                
                if (typeof this.options.onSuccess === 'function') {
                    await this.options.onSuccess();
                }
            } catch (err: unknown) {
                if (typeof this.options.onFail === 'function') {
                    await this.options.onFail(err);
                }
                console.log('ShopSwitch failed:', err);
            }
        });
    }

    private async clearCartContents(): Promise<ClearCartContentsResult> {
        const clearCartContentsUrl = this.options.clearCartContentsUrl;
        if (!clearCartContentsUrl) { return { success: true }; }
        try {
            const postData: Record<string, string> = {
                '__RequestVerificationToken': this.options.requestVerificationToken || ''
            };

            const body = new URLSearchParams(postData).toString();

            const response = await fetch(
                clearCartContentsUrl,
                {
                    credentials: 'same-origin',
                    method: 'POST',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                        'Accept': 'application/json'
                    },
                    body
                }
            );

            if (!response.ok) {
                console.warn(`Cart check failed with status ${response.status}, assuming no items`);
                return { success: false };
            }

            const data = await response.json() as { success?: boolean };
            return { success: Boolean(data.success) };
        } catch (error) {
            console.error('Error checking cart contents:', error);
            return { success: false };
        }
    }

    private async displayWarningModal(): Promise<void> {
        const $modal = $("#shop-switch-warning-modal");
        const $confirmBtn = $modal.find(".confirm-change-shops");
        $confirmBtn.attr("href", this.newShopUrl);
        $confirmBtn.off('click.shopSwitch');
        $confirmBtn.on('click.shopSwitch', async (e) => {
            e.preventDefault();
            const clearCartContentsUrl = this.options.clearCartContentsUrl;
            try {
                if (clearCartContentsUrl) {
                    const { success } = await this.clearCartContents();
                    if (!success) {
                        alert("There was an issue clearing your cart. Please try again.");
                        return;
                    }
                }
                window.location.href = this.newShopUrl;
            } catch (err) {
                console.error('Error clearing cart contents:', err);
                alert("There was an issue clearing your cart. Please try again.");
            }
        });
        
        (M2.Modal.create as any)(
            "shop-switch-warning", // id
            $modal.data('modal-title'), // title
            "#shop-switch-warning-modal", // contentSrc
            $modal.data('modal-size'), // size
            true, // isInline
        );
    }

    private async checkCartContents(): Promise<CartContentsResult> {
        const checkCartContentsUrl = this.options.checkCartContentsUrl;
        if (!checkCartContentsUrl) {
            return { hasItems: false }; 
        }
         try {
            const response = await fetch(
                checkCartContentsUrl,
                { 
                    credentials: 'same-origin',
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                }
            );

            if (!response.ok) {
                console.warn(`Cart check failed with status ${response.status}, assuming no items`);
                return { hasItems: false };
            }

            const data = await response.json() as { hasItems?: boolean };
            return { hasItems: Boolean(data.hasItems) };
        } catch (error) {
            console.error('Error checking cart contents:', error);
            return { hasItems: false };
        }
    }

    public static create(
        target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[],
        options: ShopSwitchOptions = {}
    ): ShopSwitch[] | null {

        const triggers = ComponentRegistry.processTargets<ShopSwitch, HTMLElement>(
            ShopSwitch.registry,
            target,
            [HTMLButtonElement, HTMLAnchorElement]
        ) as (HTMLButtonElement | HTMLAnchorElement)[];

        if (!triggers.length) return null;
        
        return triggers.map(trigger => new ShopSwitch(trigger, options));
    }

    public static getShopSwitch(id: string): ShopSwitch | null {
        return ShopSwitch.registry.get(id) ?? null;
    }

    public destroy(): void {
        ShopSwitch.registry.unregister(this.id);
    }
}